import { useEffect, useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';
import TagManager from 'react-gtm-module';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'hooks/use-ui';
import { isBrowser } from 'utils/misc-utils';
import { embeddedEventDispatcher } from 'utils/analytics/helpers/gtag';
import { logDataLayerPushError } from 'utils/analytics/helpers/data-layer-push-error';
import { getLogger } from 'utils/datadog-logger';

const logger = getLogger('sgtm');

export function pushGTMDataLayer(payload, includeChannel = false, channel = null) {
  const enhancedPayload = includeChannel && channel ? { ...payload, channel } : payload;

  if (window.__DEBUG_GTM) {
    console.log('[dutchie] GTM DataLayer: ', enhancedPayload);
  }
  if (window.dataLayer) {
    // Dispensary-supplied (server-side) GTM containers can monkey-patch
    // `window.dataLayer.push` to inspect every push. A bug in that third-party
    // code must not be able to throw synchronously here and take down the whole
    // page via our top-level ErrorBoundary — analytics should fail silently
    // instead of failing checkout.
    try {
      window.dataLayer.push(enhancedPayload);
    } catch (error) {
      logDataLayerPushError('dataLayer.push threw (likely a third-party GTM container script)', error);
    }
  }
  embeddedEventDispatcher('dataLayer', 'push', enhancedPayload);
}

/**
 * Get the appropriate GTM container ID for the current channel
 * @param {Object} dispensary - The dispensary object
 * @param {'storefront'|'kiosk'|'embedded'|'mobileApp'|'default'} channel - The current channel
 * @returns {string|undefined} - The GTM container ID or undefined if not configured
 */
export function getChannelSpecificGTMContainerID(dispensary, channel) {
  const gtmConfig = dispensary?.googleTagManager;

  if (!gtmConfig) {
    return undefined;
  }

  // Try channel-specific container ID first
  const channelSpecificId = gtmConfig?.containerIds?.[channel];
  if (channelSpecificId) {
    return channelSpecificId;
  }

  // Fall back to default container ID
  const defaultId = gtmConfig?.containerIds?.default;
  if (defaultId) {
    return defaultId;
  }

  // Final fallback to legacy gtmID
  return gtmConfig?.gtmID;
}

/**
 * Validate a server-side GTM (sGTM) loader URL. Only absolute https URLs are
 * accepted; anything else (including empty values or plaintext http) returns
 * undefined. https is required because the value becomes a consumer-facing
 * `<script src>` — a plaintext origin would be MITM-injectable, and Google's
 * sGTM tagging servers are https-only. The URL is used verbatim, so it is only
 * trimmed — no `/gtm.js` or `?id=` is appended.
 * @param {*} rawUrl - The candidate URL
 * @returns {string|undefined} - The validated, trimmed loader URL or undefined
 */
function validateServerContainerUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') {
    return undefined;
  }

  const trimmed = rawUrl.trim();

  if (!/^https:\/\//i.test(trimmed)) {
    return undefined;
  }

  return trimmed;
}

/**
 * Resolve and validate the legacy single server-side GTM (sGTM) loader URL for a
 * dispensary. When set, the GTM container is loaded verbatim from this
 * customer-hosted URL instead of www.googletagmanager.com. Used in legacy
 * (non-channel-specific) mode.
 * @param {Object} dispensary - The dispensary object
 * @returns {string|undefined} - The validated loader URL or undefined
 */
export function getServerContainerUrl(dispensary) {
  return validateServerContainerUrl(dispensary?.googleTagManager?.serverContainerUrl);
}

/**
 * Resolve and validate the per-channel server-side GTM (sGTM) loader URL for a
 * dispensary. Tries the channel-specific URL first, then the `default` URL.
 * Mirrors getChannelSpecificGTMContainerID. When set, it takes precedence over
 * the channel's GTM container ID.
 * @param {Object} dispensary - The dispensary object
 * @param {'storefront'|'kiosk'|'embedded'|'mobileApp'|'default'} channel - The current channel
 * @returns {string|undefined} - The validated loader URL or undefined
 */
export function getChannelSpecificServerContainerUrl(dispensary, channel) {
  const serverContainerUrls = dispensary?.googleTagManager?.serverContainerUrls;

  return (
    validateServerContainerUrl(serverContainerUrls?.[channel]) ||
    validateServerContainerUrl(serverContainerUrls?.default)
  );
}

/**
 * Determine the current channel based on UI state
 * @param {Object} UI - The UI store
 * @returns {'storefront'|'kiosk'|'embedded'|'mobileApp'|'default'} - The current channel
 */
export function getCurrentChannel(UI) {
  if (UI.isStoreFront) {
    return 'storefront';
  }
  if (UI.isKiosk) {
    return 'kiosk';
  }
  if (UI.isEmbedded) {
    return 'embedded';
  }
  if (UI.isMobileEcommApp) {
    return 'mobileApp';
  }
  return 'default';
}

// Keep track of initialized trackers to avoid duplicate initialization. Keyed by
// container ID or loader URL — the two namespaces don't collide. This assumes a
// dispensary config change (switching between a container ID and a custom URL)
// takes effect only after a page reload, which the GraphQL-driven bootstrap does.
const initializedTrackers = new Set();

const GTM_ALLOWLIST = ['google', 'customPixels', 'flc', 'fls', 'twitter_website_tag'];

// Mirrors the react-gtm-module snippet, but loads the container from a
// customer-hosted server-side GTM (sGTM) loader URL instead of
// www.googletagmanager.com. The URL is used verbatim (the customer supplies the
// full loader URL, including any custom path and the id query param).
// See https://developers.google.com/tag-platform/tag-manager/server-side
export function initServerContainerTracker(serverContainerUrl, unrestrictedTags = false) {
  if (initializedTrackers.has(serverContainerUrl)) {
    return;
  }

  window.dataLayer = window.dataLayer || [];

  // The container we're about to load is customer-supplied and may already have
  // patched dataLayer.push by the time this runs (see pushGTMDataLayer above for
  // the same isolation rationale), so these bootstrap pushes need the same
  // try/catch protection.
  //
  // Each push is guarded separately: sharing one try/catch would let a throw on
  // the allowlist push skip the gtm.start push below, and the container needs
  // that event to fire its Initialization and All Pages triggers.
  const pushBootstrapEntry = (entry) => {
    try {
      window.dataLayer.push(entry);
    } catch (error) {
      logDataLayerPushError('dataLayer.push threw while bootstrapping server-side GTM container', error, {
        entryKeys: Object.keys(entry).join(','),
        serverContainerUrl,
      });
    }
  };

  if (!unrestrictedTags) {
    pushBootstrapEntry({ 'gtm.allowlist': GTM_ALLOWLIST });
  }

  pushBootstrapEntry({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = serverContainerUrl;
  // A misconfigured/unreachable sGTM loader otherwise fails silently and kills
  // analytics for the dispensary with no signal; surface it in Datadog instead.
  script.onerror = () => {
    logger.error('Failed to load server-side GTM container', { serverContainerUrl });
  };

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  initializedTrackers.add(serverContainerUrl);
}

function initDispensaryTracker(id, unrestrictedTags = false) {
  if (!id || initializedTrackers.has(id)) {
    return;
  }

  let dataLayerConfig = {};
  if (!unrestrictedTags) {
    dataLayerConfig = {
      'gtm.allowlist': GTM_ALLOWLIST,
    };
  }

  // TagManager.initialize pushes to window.dataLayer internally, so it carries the
  // same third-party-interceptor risk as our own pushes above. A dispensary can have
  // both a container ID and a custom sGTM loader URL configured, in which case an
  // already-loaded container may have patched push before this runs.
  try {
    TagManager.initialize({
      gtmId: id,
      dataLayer: {
        ...dataLayerConfig,
      },
    });
  } catch (error) {
    logDataLayerPushError('TagManager.initialize threw (likely a third-party GTM container script)', error, {
      gtmId: id,
    });
  }

  initializedTrackers.add(id);
}

export function useDispensaryTagManager() {
  const UI = useUI();
  const { dispensary } = useDispensary();
  const ldFlags = useFlags();

  // Determine if channel-specific GTM is enabled via feature flag
  const channelSpecificGTMEnabled = ldFlags?.['ecomm.gtm.channel-specific-containers.rollout'] ?? false;

  // Gates the custom sGTM loader URL on the consumer side so the flag is an
  // effective kill-switch: flipping it off falls back to Google-hosted container
  // IDs without needing to clear each dispensary's persisted URL.
  const sgtmCustomUrlEnabled = ldFlags?.['ecomm.gtm.sgtm-custom-url.rollout'] ?? false;

  // Determine current channel
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentChannel = useMemo(() => getCurrentChannel(UI), [
    UI.isStoreFront,
    UI.isKiosk,
    UI.isEmbedded,
    UI.isMobileEcommApp,
  ]);

  // Always run dispensary GTM for embedded, storefront, plus, and kiosk when feature flag is enabled
  const shouldUseDispensaryGTM = useObserver(
    () => UI.isEmbedded || UI.isStoreFront || UI.isPlus || (channelSpecificGTMEnabled && UI.isKiosk)
  );

  return useEffect(() => {
    if (!isBrowser() || !shouldUseDispensaryGTM || !dispensary) {
      return;
    }

    const unrestrictedTags = dispensary.googleTagManager?.flags?.unrestricted_tags;

    if (channelSpecificGTMEnabled) {
      // Channel-specific mode. If a per-channel sGTM loader URL is configured for
      // this channel, load the container verbatim from it; otherwise resolve the
      // per-channel container ID and load it from Google's default servers.
      const serverContainerUrl = sgtmCustomUrlEnabled
        ? getChannelSpecificServerContainerUrl(dispensary, currentChannel)
        : undefined;
      if (serverContainerUrl) {
        initServerContainerTracker(serverContainerUrl, unrestrictedTags);
      } else {
        const gtmContainerID = getChannelSpecificGTMContainerID(dispensary, currentChannel);
        if (gtmContainerID) {
          initDispensaryTracker(gtmContainerID, unrestrictedTags);
        }
      }
    } else {
      // Legacy mode. If a server-side GTM loader URL is configured, load the
      // container verbatim from it; otherwise use the legacy single container ID
      // via the default Google-hosted loader.
      const serverContainerUrl = sgtmCustomUrlEnabled ? getServerContainerUrl(dispensary) : undefined;
      if (serverContainerUrl) {
        initServerContainerTracker(serverContainerUrl, unrestrictedTags);
      } else {
        const gtmContainerID = dispensary?.googleTagManager?.gtmID;
        if (gtmContainerID) {
          initDispensaryTracker(gtmContainerID, unrestrictedTags);
        }
      }
    }

    // Push identifying data to data layer with channel information
    pushGTMDataLayer(
      {
        retailerId: dispensary.id,
        retailerName: dispensary.name,
        retailerAddress: dispensary.address,
      },
      channelSpecificGTMEnabled,
      currentChannel
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispensary?.id,
    dispensary?.name,
    dispensary?.address,
    dispensary?.googleTagManager,
    shouldUseDispensaryGTM,
    currentChannel,
    channelSpecificGTMEnabled,
    sgtmCustomUrlEnabled,
  ]);
}
